import React, { useState, useEffect } from 'react';
import { Layout, Card, Typography, Button, Space, Modal } from 'antd';
import { ArrowLeftOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import WhatsAppOnboardingModal from './components/WhatsAppOnboardingModal';
import StepsContainer from '../../components/StepsContainer';
import ConnectStepImage from '../../assets/images/guide-step2-connect.png';
import ContactsStepImage from '../../assets/images/guide-step3-contacts.png';
import PricingStepImage from '../../assets/images/pricing-step-illustration.png';
import SendStepImage from '../../assets/images/guide-step5-send.png';
import './whatsappRegistration.css';

const { Title, Paragraph } = Typography;
const { Content } = Layout;

// Custom hook for responsive detection
const useWindowSize = () => {
  const [windowSize, setWindowSize] = useState({
    width: typeof window !== 'undefined' ? window.innerWidth : 0,
  });

  useEffect(() => {
    const handleResize = () => {
      setWindowSize({ width: window.innerWidth });
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  return windowSize;
};

const WhatsAppRegistration = () => {
  const [modalVisible, setModalVisible] = useState(false);
  const [tutorialVisible, setTutorialVisible] = useState(false);
  const { width } = useWindowSize();
  const isMobile = width <= 480;
  const navigate = useNavigate();

  const handleBack = () => {
    navigate(-1); // Go back to previous page
  };

  return (
    <Layout style={{ minHeight: '100vh', background: '#f8f9fa' }}>
      {/* Back Button */}
      <div style={{ 
        position: 'absolute', 
        top: isMobile ? 12 : 20, 
        left: isMobile ? 12 : 20, 
        zIndex: 10 
      }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={handleBack}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            fontSize: isMobile ? 14 : 16,
            color: '#128C7E',
            fontWeight: 500,
            padding: isMobile ? '6px 12px' : '8px 16px',
            height: 'auto',
            background: 'white',
            borderRadius: 8,
            boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
          }}
        >
          Back
        </Button>
      </div>

      <Content style={{ 
        padding: isMobile ? '20px 12px' : '50px 20px', 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center' 
      }}>
        <Card 
          style={{ 
            maxWidth: 500, 
            width: '100%', 
            textAlign: 'center',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
            borderRadius: isMobile ? '8px' : '12px',
            background: 'white',
            border: 'none'
          }}
        >
          <div style={{ padding: isMobile ? '24px 16px' : '40px 30px', position: 'relative' }}>
            {/* ? button - top right corner */}
            <Button
              type="text"
              shape="circle"
              icon={<QuestionCircleOutlined style={{ fontSize: 18, color: '#1890ff' }} />}
              onClick={() => setTutorialVisible(true)}
              style={{ position: 'absolute', top: 12, right: 12 }}
              title="How does it work?"
            />
            <div style={{ marginBottom: isMobile ? '20px' : '30px' }}>
            <div style={{ margin: '0 auto 20px', display: 'flex', justifyContent: 'center' }}>
              <svg
                width={isMobile ? "60" : "80"}
                height={isMobile ? "60" : "80"}
                viewBox="0 0 287.56 191"
                fill="none"
              >
                <defs>
                  <linearGradient
                    id="linear-gradient"
                    x1="62.34"
                    y1="101.45"
                    x2="260.34"
                    y2="91.45"
                    gradientTransform="matrix(1, 0, 0, -1, 0, 192)"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop offset="0" stopColor="#0064e1" />
                    <stop offset="0.4" stopColor="#0064e1" />
                    <stop offset="0.83" stopColor="#0073ee" />
                    <stop offset="1" stopColor="#0082fb" />
                  </linearGradient>
                  <linearGradient
                    id="linear-gradient-2"
                    x1="41.42"
                    y1="53"
                    x2="41.42"
                    y2="126"
                    gradientTransform="matrix(1, 0, 0, -1, 0, 192)"
                    gradientUnits="userSpaceOnUse"
                  >
                    <stop offset="0" stopColor="#0082fb" />
                    <stop offset="1" stopColor="#0064e0" />
                  </linearGradient>
                </defs>
                <path
                  d="M31.06,126c0,11,2.41,19.41,5.56,24.51A19,19,0,0,0,53.19,160c8.1,0,15.51-2,29.79-21.76,11.44-15.83,24.92-38,34-52l15.36-23.6c10.67-16.39,23-34.61,37.18-47C181.07,5.6,193.54,0,206.09,0c21.07,0,41.14,12.21,56.5,35.11,16.81,25.08,25,56.67,25,89.27,0,19.38-3.82,33.62-10.32,44.87C271,180.13,258.72,191,238.13,191V160c17.63,0,22-16.2,22-34.74,0-26.42-6.16-55.74-19.73-76.69-9.63-14.86-22.11-23.94-35.84-23.94-14.85,0-26.8,11.2-40.23,31.17-7.14,10.61-14.47,23.54-22.7,38.13l-9.06,16c-18.2,32.27-22.81,39.62-31.91,51.75C84.74,183,71.12,191,53.19,191c-21.27,0-34.72-9.21-43-23.09C3.34,156.6,0,141.76,0,124.85Z"
                  fill="#0081fb"
                />
                <path
                  d="M24.49,37.3C38.73,15.35,59.28,0,82.85,0c13.65,0,27.22,4,41.39,15.61,15.5,12.65,32,33.48,52.63,67.81l7.39,12.32c17.84,29.72,28,45,33.93,52.22,7.64,9.26,13,12,19.94,12,17.63,0,22-16.2,22-34.74l27.4-.86c0,19.38-3.82,33.62-10.32,44.87C271,180.13,258.72,191,238.13,191c-12.8,0-24.14-2.78-36.68-14.61-9.64-9.08-20.91-25.21-29.58-39.71L146.08,93.6c-12.94-21.62-24.81-37.74-31.68-45C107,40.71,97.51,31.23,82.35,31.23c-12.27,0-22.69,8.61-31.41,21.78Z"
                  fill="url(#linear-gradient)"
                />
                <path
                  d="M82.35,31.23c-12.27,0-22.69,8.61-31.41,21.78C38.61,71.62,31.06,99.34,31.06,126c0,11,2.41,19.41,5.56,24.51L10.14,167.91C3.34,156.6,0,141.76,0,124.85,0,94.1,8.44,62.05,24.49,37.3,38.73,15.35,59.28,0,82.85,0Z"
                  fill="url(#linear-gradient-2)"
                />
              </svg>
            </div>
              <Title level={2} style={{ color: '#25D366', marginBottom: '10px', fontSize: isMobile ? '20px' : undefined }}>
                WhatsApp Business Setup
              </Title>
              <Paragraph style={{ color: '#666', fontSize: isMobile ? '14px' : '16px', marginBottom: isMobile ? '20px' : '30px' }}>
                Connect your WhatsApp Business account to start messaging your customers
                with professional templates and automated responses.
              </Paragraph>
            </div>

            <Space direction="vertical" size={isMobile ? 'middle' : 'large'} style={{ width: '100%' }}>
              <Button 
                type="primary" 
                size="large" 
                style={{ 
                  height: isMobile ? '44px' : '50px', 
                  fontSize: isMobile ? '14px' : '16px',
                  background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                  border: 'none',
                  borderRadius: '8px'
                }}
                onClick={() => setModalVisible(true)}
                block
              >
                {isMobile ? 'Connect WhatsApp Account' : 'Connect WhatsApp Business Account'}
              </Button>
              
              <Paragraph style={{ color: '#999', fontSize: isMobile ? '12px' : '14px' }}>
                By connecting, you agree to our Terms of Service and Privacy Policy
              </Paragraph>
            </Space>
          </div>
        </Card>

        {/* WhatsApp Onboarding Modal */}
        <WhatsAppOnboardingModal
          visible={modalVisible}
          onClose={() => setModalVisible(false)}
          onComplete={() => {
            setModalVisible(false);
            navigate('/dashboard');
          }}
        />

        {/* Tutorial Modal - same as dashboard guide */}
        <Modal
          title={null}
          open={tutorialVisible}
          onCancel={() => setTutorialVisible(false)}
          footer={null}
          className="wapp-tutorial-modal"
          width={isMobile ? '95%' : 1000}
          centered
          bodyStyle={{
            padding: 0,
            height: isMobile ? 'auto' : 'min(600px, calc(100vh - 64px))',
            maxHeight: isMobile ? '90vh' : 'calc(100vh - 64px)',
            overflow: 'hidden'
          }}
          destroyOnClose
        >
          <StepsContainer
            steps={[
              'Create Account',
              'Connect WhatsApp',
              'Add Contacts',
              'Pricing',
              'Send Messages'
            ]}
            renderStep={(currentStep) => {
              switch (currentStep) {
                case 0:
                  return (
                    <div style={{ padding: isMobile ? '12px' : '20px' }}>
                      <h2 style={{ color: '#1890ff', marginBottom: isMobile ? '10px' : '16px', fontSize: isMobile ? '16px' : '20px' }}>Welcome to MessageOS</h2>
                      <p style={{ fontSize: isMobile ? '12px' : '15px', color: '#595959', lineHeight: '1.5' }}>
                        Get started with our platform in just a few simple steps. This guide will walk you through setting up your account and connecting your WhatsApp Business account.
                      </p>
                      <ul style={{ marginTop: isMobile ? '12px' : '20px', fontSize: isMobile ? '12px' : '14px', color: '#262626' }}>
                        <li style={{ marginBottom: isMobile ? '8px' : '12px' }}>✓ Create your company profile</li>
                        <li style={{ marginBottom: isMobile ? '8px' : '12px' }}>✓ Verify your email and phone number</li>
                        <li style={{ marginBottom: isMobile ? '8px' : '12px' }}>✓ Set up your business information</li>
                      </ul>
                    </div>
                  );
                case 1:
                  return (
                    <div style={{ padding: isMobile ? '12px' : '20px' }}>
                      <h2 style={{ color: '#1890ff', marginBottom: isMobile ? '10px' : '16px', fontSize: isMobile ? '16px' : '20px' }}>Requirements for WhatsApp Business Setup</h2>
                      <p style={{ fontSize: isMobile ? '12px' : '14px', color: '#595959', lineHeight: '1.5', marginBottom: isMobile ? '12px' : '20px' }}>
                        Before connecting your WhatsApp Business account, please ensure you have the following:
                      </p>
                      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '10px' : '16px' }}>
                        <div style={{ border: '2px solid #1890ff', borderRadius: isMobile ? '8px' : '12px', padding: isMobile ? '12px' : '20px', background: '#f0f7ff' }}>
                          <div style={{ fontSize: isMobile ? '24px' : '32px', marginBottom: isMobile ? '6px' : '12px' }}>📘</div>
                          <h4 style={{ color: '#0958d9', margin: '0 0 6px 0', fontSize: isMobile ? '13px' : '16px' }}>Facebook Page/Account</h4>
                          <p style={{ fontSize: isMobile ? '11px' : '13px', color: '#595959', margin: 0, lineHeight: '1.4' }}>A Facebook Business account or page is required to connect WhatsApp Business API</p>
                        </div>
                        <div style={{ border: '2px solid #52c41a', borderRadius: isMobile ? '8px' : '12px', padding: isMobile ? '12px' : '20px', background: '#f6ffed' }}>
                          <div style={{ fontSize: isMobile ? '24px' : '32px', marginBottom: isMobile ? '6px' : '12px' }}>📱</div>
                          <h4 style={{ color: '#389e0d', margin: '0 0 6px 0', fontSize: isMobile ? '13px' : '16px' }}>New Mobile Number</h4>
                          <p style={{ fontSize: isMobile ? '11px' : '13px', color: '#595959', margin: 0, lineHeight: '1.4' }}>A dedicated phone number that is not registered on WhatsApp or WhatsApp Business app</p>
                        </div>
                        <div style={{ border: '2px solid #722ed1', borderRadius: isMobile ? '8px' : '12px', padding: isMobile ? '12px' : '20px', background: '#f9f0ff' }}>
                          <div style={{ fontSize: isMobile ? '24px' : '32px', marginBottom: isMobile ? '6px' : '12px' }}>🔒</div>
                          <h4 style={{ color: '#531dab', margin: '0 0 6px 0', fontSize: isMobile ? '13px' : '16px' }}>HTTPS Website</h4>
                          <p style={{ fontSize: isMobile ? '11px' : '13px', color: '#595959', margin: 0, lineHeight: '1.4' }}>Your business website must have a valid SSL certificate (https://)</p>
                        </div>
                        <div style={{ border: '2px solid #fa8c16', borderRadius: isMobile ? '8px' : '12px', padding: isMobile ? '12px' : '20px', background: '#fff7e6' }}>
                          <div style={{ fontSize: isMobile ? '24px' : '32px', marginBottom: isMobile ? '6px' : '12px' }}>💳</div>
                          <h4 style={{ color: '#d46b08', margin: '0 0 6px 0', fontSize: isMobile ? '13px' : '16px' }}>Payment Card</h4>
                          <p style={{ fontSize: isMobile ? '11px' : '13px', color: '#595959', margin: 0, lineHeight: '1.4' }}>A valid credit/debit card for Facebook Business Manager verification</p>
                        </div>
                      </div>
                      <div style={{ marginTop: isMobile ? '12px' : '20px', padding: isMobile ? '10px' : '16px', background: '#fffbe6', border: '1px solid #ffe58f', borderRadius: isMobile ? '6px' : '8px' }}>
                        <p style={{ fontSize: isMobile ? '11px' : '13px', color: '#595959', margin: 0, lineHeight: '1.4' }}>
                          <strong style={{ color: '#d48806' }}>💡 Tip:</strong> Once you have all requirements ready, click "Connect WhatsApp Business Account" to start the setup process.
                        </p>
                      </div>
                    </div>
                  );
                case 2:
                  return (
                    <div style={{ padding: isMobile ? '12px' : '20px' }}>
                      <h2 style={{ color: '#1890ff', marginBottom: isMobile ? '10px' : '16px', fontSize: isMobile ? '16px' : '20px' }}>Add Your Contacts</h2>
                      <p style={{ fontSize: isMobile ? '12px' : '15px', color: '#595959', lineHeight: '1.5' }}>
                        Import your customer contacts to start messaging them. You can add contacts individually or upload them in bulk using a CSV file.
                      </p>
                      <div style={{ marginTop: isMobile ? '12px' : '20px' }}>
                        <h4 style={{ color: '#262626', marginBottom: isMobile ? '8px' : '12px', fontSize: isMobile ? '14px' : '16px' }}>Import Methods:</h4>
                        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: isMobile ? '10px' : '16px' }}>
                          <div style={{ border: '1px solid #e8e8e8', padding: isMobile ? '12px' : '16px', borderRadius: isMobile ? '6px' : '8px' }}>
                            <h5 style={{ color: '#1890ff', fontSize: isMobile ? '13px' : '15px' }}>📝 Manual Entry</h5>
                            <p style={{ fontSize: isMobile ? '11px' : '13px', color: '#595959', margin: '4px 0 0' }}>Add contacts one by one with their details</p>
                          </div>
                          <div style={{ border: '1px solid #e8e8e8', padding: isMobile ? '12px' : '16px', borderRadius: isMobile ? '6px' : '8px' }}>
                            <h5 style={{ color: '#52c41a', fontSize: isMobile ? '13px' : '15px' }}>📁 CSV Upload</h5>
                            <p style={{ fontSize: isMobile ? '11px' : '13px', color: '#595959', margin: '4px 0 0' }}>Import multiple contacts at once</p>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                case 3:
                  return (
                    <div style={{ padding: isMobile ? '12px' : '20px', overflowY: 'auto', maxHeight: isMobile ? '70vh' : '540px' }}>
                      <div style={{ marginBottom: '1.75rem' }}>
                        <h2 style={{ fontSize: isMobile ? '16px' : '18px', fontWeight: 500, color: '#1a1a1a', marginBottom: '.25rem' }}>Simple, transparent pricing</h2>
                        <p style={{ fontSize: isMobile ? '12px' : '13px', color: '#555' }}>Know exactly what you pay — platform fees + Meta charges.</p>
                      </div>
                      <div style={{ fontSize: '11px', fontWeight: 500, letterSpacing: '.08em', textTransform: 'uppercase', color: '#888', marginBottom: '.75rem' }}>Platform fees (what we charge)</div>
                      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3,1fr)', gap: '10px', marginBottom: '1.5rem' }}>
                        <div style={{ border: '.5px solid #e5e5e5', borderLeft: '3px solid #D4537E', borderRadius: '0 12px 12px 0', padding: '1rem 1.1rem' }}><div style={{ fontSize: '12px', color: '#555', marginBottom: '2px' }}>Marketing</div><div style={{ fontSize: '22px', fontWeight: 500, color: '#D4537E' }}>₹ 0.02 / msg</div><div style={{ fontSize: '12px', color: '#999' }}>per message sent</div></div>
                        <div style={{ border: '.5px solid #e5e5e5', borderLeft: '3px solid #378ADD', borderRadius: '0 12px 12px 0', padding: '1rem 1.1rem' }}><div style={{ fontSize: '12px', color: '#555', marginBottom: '2px' }}>Utility</div><div style={{ fontSize: '22px', fontWeight: 500, color: '#378ADD' }}>₹ 1.00 / msg</div><div style={{ fontSize: '12px', color: '#999' }}>per message sent</div></div>
                        <div style={{ border: '.5px solid #e5e5e5', borderLeft: '3px solid #639922', borderRadius: '0 12px 12px 0', padding: '1rem 1.1rem' }}><div style={{ fontSize: '12px', color: '#555', marginBottom: '2px' }}>Authentication</div><div style={{ fontSize: '22px', fontWeight: 500, color: '#639922' }}>₹ 0.30 / msg</div><div style={{ fontSize: '12px', color: '#999' }}>per message sent</div></div>
                      </div>
                      <div style={{ fontSize: '11px', fontWeight: 500, letterSpacing: '.08em', textTransform: 'uppercase', color: '#888', marginBottom: '.75rem' }}>Subscription plans</div>
                      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3,1fr)', gap: '10px' }}>
                        <div style={{ border: '.5px solid #e5e5e5', borderRadius: '12px', padding: '1rem 1.1rem' }}><div style={{ fontSize: '13px', fontWeight: 500, color: '#555', marginBottom: '.3rem' }}>Starter</div><div style={{ fontSize: '20px', fontWeight: 500 }}>₹ 999 <span style={{ fontSize: '12px', color: '#999' }}>/ month</span></div><div style={{ fontSize: '11px', color: '#555', marginTop: '.5rem', paddingTop: '.5rem', borderTop: '.5px solid #e5e5e5' }}>₹ 1,000 wallet credits · 1,000 msgs/mo</div></div>
                        <div style={{ border: '2px solid #378ADD', borderRadius: '12px', padding: '1rem 1.1rem' }}><span style={{ fontSize: '10px', fontWeight: 500, padding: '2px 8px', borderRadius: '4px', background: '#e6f1fb', color: '#185FA5' }}>Most popular</span><div style={{ fontSize: '13px', fontWeight: 500, color: '#555', margin: '.4rem 0 .3rem' }}>Professional</div><div style={{ fontSize: '20px', fontWeight: 500 }}>₹ 2,499 <span style={{ fontSize: '12px', color: '#999' }}>/ month</span></div><div style={{ fontSize: '11px', color: '#555', marginTop: '.5rem', paddingTop: '.5rem', borderTop: '.5px solid #e5e5e5' }}>₹ 5,000 wallet credits · 5,000 msgs/mo</div></div>
                        <div style={{ border: '.5px solid #e5e5e5', borderRadius: '12px', padding: '1rem 1.1rem' }}><div style={{ fontSize: '13px', fontWeight: 500, color: '#555', marginBottom: '.3rem' }}>Enterprise</div><div style={{ fontSize: '20px', fontWeight: 500 }}>₹ 4,999 <span style={{ fontSize: '12px', color: '#999' }}>/ month</span></div><div style={{ fontSize: '11px', color: '#555', marginTop: '.5rem', paddingTop: '.5rem', borderTop: '.5px solid #e5e5e5' }}>₹ 15,000 wallet credits · 15,000 msgs/mo</div></div>
                      </div>
                    </div>
                  );
                case 4:
                  return (
                    <div style={{ padding: isMobile ? '12px' : '20px' }}>
                      <h2 style={{ color: '#1890ff', marginBottom: isMobile ? '10px' : '16px', fontSize: isMobile ? '16px' : '20px' }}>Start Sending Messages</h2>
                      <p style={{ fontSize: isMobile ? '12px' : '15px', color: '#595959', lineHeight: '1.5' }}>
                        You're all set! Now you can start sending messages to your customers. Create templates, broadcast messages, and manage conversations.
                      </p>
                      <div style={{ marginTop: isMobile ? '12px' : '24px', display: 'flex', flexDirection: 'column', gap: isMobile ? '10px' : '14px' }}>
                        {[['📋','Templates','Create and manage message templates'],['📢','Broadcasts','Send messages to multiple contacts'],['💬','Inbox','Manage conversations with customers']].map(([icon, title, desc]) => (
                          <div key={title} style={{ display: 'flex', alignItems: 'start', gap: isMobile ? '8px' : '12px' }}>
                            <span style={{ fontSize: isMobile ? '16px' : '20px' }}>{icon}</span>
                            <div><h5 style={{ margin: 0, color: '#262626', fontSize: isMobile ? '13px' : '15px' }}>{title}</h5><p style={{ fontSize: isMobile ? '11px' : '13px', color: '#595959', margin: '2px 0 0' }}>{desc}</p></div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                default:
                  return null;
              }
            }}
            imageUrl={["/vertical-transparent-light.png", ConnectStepImage, ContactsStepImage, PricingStepImage, SendStepImage]}
            imagePosition="left"
            onFinish={() => setTutorialVisible(false)}
            finishButtonText="Got it!"
          />
        </Modal>
      </Content>
    </Layout>
  );
};

export default WhatsAppRegistration;